/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState, useEffect } from "react";
import dayjs from "dayjs";
import { useEnvironmentBudgetQuery } from "~/generated/graphql";
import { generateBudget, sanitizeLastYearSpend } from "~/components/Budgets";
import { dateFormat } from "~/constants";
import { useConvertFromBaseCurrency, useCurrency } from "~/hooks";
import { useCreateBudget } from "./useCreateBudget";
import { useEditBudget } from "./useEditBudget";

export function useEnvironmentBudget(id: string) {
  const { currency } = useCurrency();
  const { convert, rate } = useConvertFromBaseCurrency();
  const [year, setYear] = useState(dayjs().format(dateFormat.year));
  const [budget, setBudget] = useState<string[]>([]);
  const [isGenerated, setIsGenerated] = useState(false);

  const { data, loading, error } = useEnvironmentBudgetQuery({
    variables: {
      id,
      year,
      previousYear: (parseInt(year, 10) - 1).toString(),
    },
  });

  const [createEnvironmentBudget] = useCreateBudget();
  const [updateEnvironmentBudget] = useEditBudget();

  const lastYearSpends = (data?.environment?.spendYearly.spends ?? []).map(
    convert
  );
  const forecastSpendYearly = (
    data?.environment?.forecastSpendYearly.spends ?? []
  ).map(convert);

  useEffect(() => {
    setBudget(
      (data?.environment?.budgetYearly.budgetByMonth ?? []).map(convert)
    );
  }, [rate, setBudget, data]);

  return {
    budget,
    spends: lastYearSpends.map(Number),
    forecast: forecastSpendYearly.map(Number),
    loading,
    error,
    createEnvironmentBudget: async (value: number) => {
      await createEnvironmentBudget({
        nid: id,
        year,
        budgetValue: value,
        currency,
      });
      setIsGenerated(false);
    },
    updateEnvironmentBudget: async (values: number[]) => {
      await updateEnvironmentBudget({
        nid: id,
        year,
        budgetByMonth: values,
        currency,
      });
      setIsGenerated(false);
    },
    year,
    setYear: (value: string) => {
      setYear(value);
      setIsGenerated(false);
    },
    getGeneratedBudget: (initial = true) => {
      setBudget(
        sanitizeLastYearSpend(
          generateBudget(lastYearSpends, forecastSpendYearly, year)
        )
      );
      setIsGenerated(initial);
    },
    reset: (cached: number[][]) => {
      if (isGenerated) {
        setBudget([]);
        setIsGenerated(false);
      } else {
        setBudget(cached.flat().map(String));
      }
    },
    isBudgetGenerated: isGenerated,
  };
}
